import { getComponentJobCompanionPaths } from '@secureblob/sdk/componentJobs'
import type { FileStat } from '../engine/types'

/** Collect all file paths that are "companions" of a given path.
 *  foo.md → foo.md.attachments/*
 *  x.agent.json → x.agent.json.transcripts/*
 *  myjob.ts → myjob.ts.job.json + myjob.ts.runs/* + myjob.ts.builds/*  */
export function getCompanionFiles(path: string, allFiles: FileStat[]): string[] {
    const companions = new Set<string>()

    function collectPathAndDescendants(rootPath: string) {
        for (const f of allFiles) {
            if (f.path === rootPath || f.path.startsWith(rootPath + '/')) {
                companions.add(f.path)
            }
        }
    }

    collectPathAndDescendants(path + '.attachments')

    if (path.endsWith('.agent.json')) {
        collectPathAndDescendants(path + '.transcripts')
    }

    try {
        const { settings, runsDir, buildsDir } = getComponentJobCompanionPaths(path)
        collectPathAndDescendants(settings)
        collectPathAndDescendants(runsDir)
        collectPathAndDescendants(buildsDir)
    } catch {
        // Not every file path is a component-job source file, so ignore validation failures here.
    }

    return [...companions]
}

function getDirectoryContents(dirPath: string, allFiles: FileStat[]): string[] {
    const prefix = dirPath.endsWith('/') ? dirPath : dirPath + '/'
    return allFiles.filter(f => f.path.startsWith(prefix)).map(f => f.path)
}

/** Rename a file or directory, cascading to children and companions.
 *  Returns an array of [oldPath, newPath] pairs for every rename performed
 *  (including the primary file, directory children, and companions). */
export function cascadeRename(
    oldPath: string,
    newPath: string,
    files: FileStat[],
    renameFile: (o: string, n: string) => void
): [string, string][] {
    const ops: [string, string][] = []

    function doRename(o: string, n: string) {
        renameFile(o, n)
        ops.push([o, n])
    }

    doRename(oldPath, newPath)

    const isDir = files.find(f => f.path === oldPath)?.kind === 'directory'
        || files.some(f => f.path.startsWith(oldPath + '/'))

    if (isDir) {
        const oldPrefix = oldPath.endsWith('/') ? oldPath : oldPath + '/'
        const newPrefix = newPath.endsWith('/') ? newPath : newPath + '/'

        const children = files.filter(f => f.path.startsWith(oldPrefix))
        for (const child of children) {
            if (!child.path.startsWith(oldPrefix)) continue
            const suffix = child.path.slice(oldPrefix.length)
            doRename(child.path, newPrefix + suffix)
        }
    }

    // Find companions at their CURRENT location by searching at both oldPath-
    // derived and newPath-derived locations.  The direction (forward rename
    // or undo) is determined by whether the newPath main file entry exists:
    //   - If newPath IS in files (forward was applied): undo — rename companions
    //     from their current location back to their original locations.
    //   - If newPath is NOT in files (forward not yet applied): forward rename.
    const companionsAtOld = getCompanionFiles(oldPath, files)
    const companionsAtNew = getCompanionFiles(newPath, files)
    // Detect undo: if newPath already exists in the file list, the forward
    // rename of the main file was already applied (oldPath was renamed to
    // newPath).  We are undoing.  Otherwise, this is a forward rename.
    const isUndo = files.some(f => f.path === newPath)
    const seen = new Set<string>()
    for (const companionPath of [...companionsAtOld, ...companionsAtNew]) {
        if (seen.has(companionPath)) continue
        seen.add(companionPath)
        if (isUndo) {
            // Undo: rename companion from its current location back to its
            // original location (reverse the forward rename).
            const originalCompanionLocation = companionPath.replace(newPath, oldPath)
            doRename(companionPath, originalCompanionLocation)
        } else {
            // Forward rename: rename companion from its current location to
            // its new location.
            const expectedNewLocation = companionPath.replace(oldPath, newPath)
            doRename(companionPath, expectedNewLocation)
        }
    }

    return ops
}

/** Collect all paths to delete (target + children + companions). */
export function collectDeleteTargets(path: string, files: FileStat[]): string[] {
    const targets = new Set<string>()
    targets.add(path)
    getDirectoryContents(path, files).forEach(p => targets.add(p))
    getCompanionFiles(path, files).forEach(p => targets.add(p))

    const children = getDirectoryContents(path, files)
    for (const child of children) {
        getCompanionFiles(child, files).forEach(p => targets.add(p))
    }

    return [...targets]
}
